import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

import { revampMenuListStyles, revampMenuSelectButtonStyles } from 'styles/revampFormFieldStyles';
import { accent, fontSize, text } from 'styles/revampTokens';

export type RevampMenuSelectItem = { value: string; label: string };

export type RevampMenuSelectGroup = { label: string; items: RevampMenuSelectItem[] };

export interface RevampMenuSelectProperties {
  id?: string;
  'aria-labelledby'?: string;
  value: string;
  onChange: (value: string) => void;
  items?: RevampMenuSelectItem[];
  groups?: RevampMenuSelectGroup[];
  placeholder?: string;
}

function resolveLabel(
  value: string,
  items: RevampMenuSelectItem[],
  groups: RevampMenuSelectGroup[],
): string {
  const flat = [...items, ...groups.flatMap((group) => group.items)];
  return flat.find((row) => row.value === value)?.label ?? value;
}

/**
 * Revamp-styled single-select: menu list matches modals; chevron stays inside the field on the right.
 */
const RevampMenuSelect = ({
  id,
  'aria-labelledby': ariaLabelledBy,
  value,
  onChange,
  items = [],
  groups = [],
  placeholder = 'Select…',
}: RevampMenuSelectProperties) => {
  const display = value ? resolveLabel(value, items, groups) : placeholder;

  return (
    <Box w='100%'>
      <Menu matchWidth closeOnSelect>
        <MenuButton
          id={id}
          aria-labelledby={ariaLabelledBy}
          as={Button}
          rightIcon={<ChevronDownIcon boxSize={4} color={accent.solid} flexShrink={0} />}
          iconSpacing={2}
          {...revampMenuSelectButtonStyles}
        >
          <Text
            as='span'
            flex='1'
            minW={0}
            textAlign='left'
            fontWeight={500}
            color={value ? text.primary : text.muted}
            noOfLines={1}
          >
            {display}
          </Text>
        </MenuButton>
        <MenuList {...revampMenuListStyles}>
          <MenuOptionGroup type='radio' value={value}>
            {items.map((item) => (
              <MenuItemOption
                key={item.value}
                value={item.value}
                fontSize={fontSize.body}
                fontFamily={revampMenuSelectButtonStyles.fontFamily}
                onClick={() => onChange(item.value)}
              >
                {item.label}
              </MenuItemOption>
            ))}
            {groups.map((group) => (
              <MenuGroup key={group.label} title={group.label}>
                {group.items.map((item) => (
                  <MenuItemOption
                    key={item.value}
                    value={item.value}
                    fontSize={fontSize.body}
                    fontFamily={revampMenuSelectButtonStyles.fontFamily}
                    onClick={() => onChange(item.value)}
                  >
                    {item.label}
                  </MenuItemOption>
                ))}
              </MenuGroup>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </Menu>
    </Box>
  );
};

export default RevampMenuSelect;
